import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

/**
 * Generation Progress Controller
 * Subscribes to ActionCable channel and updates UI with generation progress
 */
export default class extends Controller {
  static values = {
    configId: Number,
    channel: String,
    previewUrl: String,
    startUrl: String,
    confirmVideoUrl: String,
    confirmCharactersUrl: String,
    regenerateAnchorUrl: String
  }

  static targets = [
    "statusText",
    "message",
    "percentage",
    "progressBar",
    "progressContainer",
    "continueButton",
    "steps",
    "imageCount",
    "imageTotalCount",
    "errorContainer",
    "errorMessage",
    "characterConfirmation",
    "characterAnchors"
  ]

  connect() {
    this.subscribeToChannel()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  get effectiveConfigId() {
    if (this.hasConfigIdValue && this.configIdValue) {
      return this.configIdValue
    }
    // Extract from channel name like "shorts_wizard_16"
    if (this.hasChannelValue && this.channelValue) {
      const match = this.channelValue.match(/(\d+)$/)
      return match ? parseInt(match[1], 10) : null
    }
    return null
  }

  subscribeToChannel() {
    const configId = this.effectiveConfigId
    if (!configId) {
      console.error("generation-progress: no configId or channel value provided")
      return
    }

    const controller = this

    this.subscription = consumer.subscriptions.create(
      { channel: "ShortsWizardChannel", config_id: configId },
      {
        connected() {
          console.log("Connected to ShortsWizardChannel")
          controller.triggerGeneration()
        },

        disconnected() {
          console.log("Disconnected from ShortsWizardChannel")
        },

        received(data) {
          controller.handleProgress(data)
        }
      }
    )
  }

  async triggerGeneration() {
    if (!this.startUrlValue) return

    try {
      const response = await fetch(this.startUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Content-Type": "application/json"
        }
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        this.showError(data.error || "Failed to start generation")
      }
    } catch (error) {
      this.showError("Failed to start generation. Please try again.")
    }
  }

  handleProgress(data) {
    const { step, percentage, message, error, image_count, total_images } = data

    if (error) {
      this.showError(message)
      return
    }

    // Update percentage
    if (this.hasPercentageTarget) {
      this.percentageTarget.textContent = `${percentage}%`
    }

    // Update progress bar
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`
    }

    // Update status text / message
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = message
    }
    if (this.hasMessageTarget) {
      this.messageTarget.textContent = message
    }

    // Update image count from broadcast data
    if (image_count != null && this.hasImageCountTarget) {
      this.imageCountTarget.textContent = image_count
    }

    // Update total image count (for image density modes)
    if (total_images != null && this.hasImageTotalCountTarget) {
      this.imageTotalCountTarget.textContent = total_images
    }

    // Update step indicators
    this.updateStepIndicator(step)

    // Handle character confirmation pause
    if (step === "characters_ready") {
      this.handleCharactersReady(data)
      return
    }

    // Handle video cost confirmation (auto-video mode)
    if (step === "video_confirmation") {
      this.handleVideoConfirmation(data)
      return
    }

    // Handle completion
    if (step === "complete") {
      this.handleCompletion()
    }
  }

  updateStepIndicator(currentStep) {
    if (!this.hasStepsTarget) return

    const steps = ["preparing", "split_script", "generate_images", "generate_tts", "extract_timings", "assemble"]
    const currentIndex = steps.indexOf(currentStep)

    steps.forEach((step, index) => {
      const stepEl = this.stepsTarget.querySelector(`[data-step="${step}"]`)
      if (!stepEl) return

      const indicator = stepEl.querySelector(".rounded-full")
      const checkmark = indicator?.querySelector("svg")

      if (index < currentIndex) {
        // Completed step
        indicator?.classList.remove("border-gray-300")
        indicator?.classList.add("bg-indigo-600", "border-indigo-600")
        checkmark?.classList.remove("hidden")
        stepEl.classList.remove("text-gray-500")
        stepEl.classList.add("text-indigo-600")
      } else if (index === currentIndex) {
        // Current step
        indicator?.classList.remove("border-gray-300")
        indicator?.classList.add("border-indigo-600")
        stepEl.classList.remove("text-gray-500")
        stepEl.classList.add("text-indigo-600")
      }
    })
  }

  async handleVideoConfirmation(data) {
    const { video_cost_usd, video_segment_count, video_total_seconds, video_model_key } = data
    const costDisplay = `$${parseFloat(video_cost_usd).toFixed(2)}`

    const confirmed = confirm(
      `Generate AI video backgrounds for ${video_segment_count} scene(s)?\n\n` +
      `Total video: ~${video_total_seconds}s\n` +
      `Estimated cost: ${costDisplay}\n\n` +
      `Press OK to generate videos, or Cancel to skip (you can add videos manually later on the preview page).`
    )

    if (confirmed && this.confirmVideoUrlValue) {
      if (this.hasStatusTextTarget) {
        this.statusTextTarget.textContent = "Starting video generation..."
      }

      try {
        const response = await fetch(this.confirmVideoUrlValue, {
          method: "POST",
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ model_key: video_model_key })
        })

        const result = await response.json()
        if (response.ok && result.success) {
          if (this.hasStatusTextTarget) {
            this.statusTextTarget.textContent =
              `Generating ${result.data.queued_count} video clips... This will continue in the background.`
          }
        } else {
          if (this.hasStatusTextTarget) {
            this.statusTextTarget.textContent = result.error || "Failed to start video generation"
          }
        }
      } catch {
        if (this.hasStatusTextTarget) {
          this.statusTextTarget.textContent = "Failed to start video generation"
        }
      }
    }
    // Continue to completion regardless (videos generate in background)
  }

  handleCharactersReady(data) {
    const { characters } = data

    // Hide progress, show character confirmation
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.add("hidden")
    }
    if (this.hasCharacterConfirmationTarget) {
      this.characterConfirmationTarget.classList.remove("hidden")
    }

    // Render character anchor cards
    if (this.hasCharacterAnchorsTarget && characters) {
      this.characterAnchorsTarget.innerHTML = characters.map(char => {
        const anchorUrl = char.anchor_path
          ? `/storage/ai_media/${char.anchor_path.split("/storage/ai_media/").pop()}`
          : ""
        return `
          <div class="shrink-0 w-24">
            <div class="h-32 rounded-lg overflow-hidden border border-indigo-200 shadow-xs bg-gray-100">
              ${anchorUrl ? `<img src="${anchorUrl}" alt="${char.label}" class="w-full h-full object-cover">` : ""}
            </div>
            <p class="mt-1 text-xs font-medium text-indigo-800 truncate text-center">${char.label}</p>
          </div>
        `
      }).join("")
    }
  }

  async approveCharacters() {
    if (!this.confirmCharactersUrlValue) return

    // Show progress again
    if (this.hasCharacterConfirmationTarget) {
      this.characterConfirmationTarget.classList.add("hidden")
    }
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.remove("hidden")
    }
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = "Generating scene images..."
    }

    try {
      const response = await fetch(this.confirmCharactersUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ confirmed: true })
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        this.showError(data.error || "Failed to continue generation")
      }
    } catch {
      this.showError("Failed to continue generation. Please try again.")
    }
  }

  async regenerateCharacters() {
    if (!this.confirmCharactersUrlValue) return

    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = "Regenerating character references..."
    }

    // Show a loading state on the anchors
    if (this.hasCharacterAnchorsTarget) {
      this.characterAnchorsTarget.querySelectorAll("img").forEach(img => {
        img.style.opacity = "0.4"
      })
    }

    try {
      const response = await fetch(this.confirmCharactersUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ regenerate: true })
      })

      const result = await response.json()
      if (response.ok && result.success) {
        // Reload page to show new anchors (simplest approach)
        window.location.reload()
      } else {
        this.showError(result.error || "Failed to regenerate characters")
      }
    } catch {
      this.showError("Failed to regenerate characters. Please try again.")
    }
  }

  handleCompletion() {
    // Enable continue button if it exists
    if (this.hasContinueButtonTarget) {
      this.continueButtonTarget.classList.remove("opacity-50", "pointer-events-none")
    }

    // Redirect to preview after a short delay
    setTimeout(() => {
      if (this.previewUrlValue) {
        window.location.href = this.previewUrlValue
      }
    }, 1000)
  }

  showError(message) {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.classList.remove("hidden")
    }

    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
    }

    // Hide progress spinner if visible
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.add("hidden")
    }

    // Reset progress bar to show error state
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.classList.remove("bg-indigo-600")
      this.progressBarTarget.classList.add("bg-red-600")
    }
  }
}
